import {
  isAllowedClaudeModel,
  isAllowedGeminiImageModel,
  isAllowedGeminiTextModel,
  isAllowedGrokModel,
  isAllowedOpenAiModel,
} from '@shared/models/ai/ai-models.model';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { isCadTextExtension } from '@shared/helpers/design/cad-source-text.helpers';
import type { PdfGeometryImportProvider } from '@shared/models/design/pdf-geometry-import.model';

export const GEOMETRY_IMPORT_EXTENSIONS = Object.freeze([
  '.pdf',
  '.dwg',
  '.dxf',
  '.ifc',
  '.ifczip',
  '.rvt',
] as const);

export type GeometryImportExtension =
  (typeof GEOMETRY_IMPORT_EXTENSIONS)[number];

/** ASCII CAD kind used to pick DXF vs IFC reading rules in the import prompt. */
export type GeometryImportCadKind = 'dxf' | 'ifc';

/**
 * How a file reaches the model.
 *
 * - `pdf`: uploaded to the provider Files API (Gemini, OpenAI, Grok) or sent
 *   as an inline document (Claude).
 * - `cad-text`: ASCII CAD export, reduced to geometry and sent as source text.
 * - `binary`: proprietary binary that no provider can read.
 */
export type GeometryImportSourceKind = 'pdf' | 'cad-text' | 'binary';

export type GeometryImportSupportState =
  'supported' | 'experimental' | 'unsupported';

export type GeometryImportSupportResult = {
  state: GeometryImportSupportState;
  message?: string;
};

const GEOMETRY_IMPORT_EXTENSIONS_SET: ReadonlySet<string> = new Set(
  GEOMETRY_IMPORT_EXTENSIONS,
);

const PDF_EXTENSION: GeometryImportExtension = '.pdf';

/** Formats to suggest when the dropped file cannot be read at all. */
const CONVERTIBLE_FORMATS_HINT = 'Export it as PDF or DXF and drop it again.';

const isSupportedGeometryImportExtension = (
  extension: string,
): extension is GeometryImportExtension => {
  return GEOMETRY_IMPORT_EXTENSIONS_SET.has(extension);
};

const getFileExtension = (filename: string): string => {
  const dotIndex = filename.lastIndexOf('.');
  if (dotIndex < 0) return '';
  return filename.slice(dotIndex).toLowerCase();
};

export const getGeometryImportExtension = (
  filename: string,
): GeometryImportExtension | undefined => {
  const extension = getFileExtension(filename);
  if (!isSupportedGeometryImportExtension(extension)) return undefined;
  return extension;
};

type GeometryImportFile = {
  filename: string;
  mimeType?: string;
};

/**
 * DXF vs IFC for CAD source-text prompts. PDF/DWG/RVT have no CAD kind.
 */
export const getGeometryImportCadKind = (
  filename: string,
): GeometryImportCadKind | undefined => {
  const extension = getGeometryImportExtension(filename);
  switch (extension) {
    case '.dxf':
      return 'dxf';
    case '.ifc':
    case '.ifczip':
      return 'ifc';
    case '.pdf':
    case '.dwg':
    case '.rvt':
    case undefined:
      return undefined;
    default:
      return assertUnreachable(extension);
  }
};

/**
 * Prompt `sourceForm` for a dropped file. Binary formats still map to
 * `attachment` so an unsupported-file prompt stays PDF-shaped.
 */
export const getGeometryImportPromptSourceForm = (
  sourceKind: GeometryImportSourceKind,
): 'attachment' | 'cad-source-text' => {
  switch (sourceKind) {
    case 'cad-text':
      return 'cad-source-text';
    case 'pdf':
    case 'binary':
      return 'attachment';
    default:
      return assertUnreachable(sourceKind);
  }
};

/**
 * Classify how a dropped file can be handed to the model.
 */
export const getGeometryImportSourceKind = (
  file: GeometryImportFile,
): GeometryImportSourceKind => {
  const extension = getGeometryImportExtension(file.filename);
  if (extension === PDF_EXTENSION) return 'pdf';
  // Browsers sometimes omit the extension but still report the PDF MIME type.
  if (extension === undefined && file.mimeType === 'application/pdf') {
    return 'pdf';
  }
  if (extension === '.ifczip') return 'cad-text';
  if (extension !== undefined && isCadTextExtension(extension)) {
    return 'cad-text';
  }
  return 'binary';
};

type GeometryImportSupportInput = GeometryImportFile & {
  provider: PdfGeometryImportProvider;
  model?: string;
};

/**
 * Central provider/model capability matrix for geometry import.
 *
 * Gemini, OpenAI, and Claude read a PDF as a full document. Grok uploads the
 * PDF through the xAI Files API, which runs as document search rather than
 * full-drawing vision — so Grok+PDF is experimental. All providers can reason
 * about DXF/IFC source text (`.ifczip` is unzipped to `.ifc` first). Gemini
 * image models cannot return JSON; other models must be on the geometry-import
 * allowlists.
 */
export const getGeometryImportSupport = (
  input: GeometryImportSupportInput,
): GeometryImportSupportResult => {
  const kind = getGeometryImportSourceKind(input);
  if (kind === 'binary') {
    return {
      state: 'unsupported',
      message: `This format is a proprietary binary that no AI provider can read. ${CONVERTIBLE_FORMATS_HINT}`,
    };
  }

  const modelSupport = getGeometryImportModelSupport(input);
  if (modelSupport) return modelSupport;

  switch (kind) {
    case 'pdf':
      return getGeometryImportPdfSupport(input.provider);
    case 'cad-text':
      return {
        state: 'experimental',
        message:
          'CAD drawings are exploded to world-space outline text. Extraction quality varies — PDF is the most reliable option.',
      };
    default:
      return assertUnreachable(kind);
  }
};

/**
 * Grok PDF attachments use xAI document search, not a full-page read.
 */
const getGeometryImportPdfSupport = (
  provider: PdfGeometryImportProvider,
): GeometryImportSupportResult => {
  switch (provider) {
    case 'grok':
      return {
        state: 'experimental',
        message:
          'Grok reads PDFs via document search rather than the full drawing. Extraction quality varies — Gemini, OpenAI, or Claude is more reliable for plans.',
      };
    case 'gemini':
    case 'openai':
    case 'claude':
      return { state: 'supported' };
    default:
      return assertUnreachable(provider);
  }
};

/**
 * Reject Gemini image models and any model outside the import allowlists.
 */
const getGeometryImportModelSupport = (
  input: GeometryImportSupportInput,
): GeometryImportSupportResult | undefined => {
  if (input.model === undefined) return undefined;

  switch (input.provider) {
    case 'gemini':
      if (isAllowedGeminiImageModel(input.model)) {
        return {
          state: 'unsupported',
          message:
            'Geometry import needs a JSON answer, so pick a Gemini text model instead of an image model.',
        };
      }
      if (!isAllowedGeminiTextModel(input.model)) {
        return {
          state: 'unsupported',
          message: 'Geometry import needs an allowed Gemini text model.',
        };
      }
      return undefined;
    case 'openai':
      if (!isAllowedOpenAiModel(input.model)) {
        return {
          state: 'unsupported',
          message: 'Geometry import needs an allowed OpenAI model.',
        };
      }
      return undefined;
    case 'claude':
      if (!isAllowedClaudeModel(input.model)) {
        return {
          state: 'unsupported',
          message: 'Geometry import needs an allowed Claude model.',
        };
      }
      return undefined;
    case 'grok':
      if (!isAllowedGrokModel(input.model)) {
        return {
          state: 'unsupported',
          message: 'Geometry import needs an allowed Grok model.',
        };
      }
      return undefined;
    default:
      return assertUnreachable(input.provider);
  }
};
